import { useCallback, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';

import { useToast } from 'Shared/components';
import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import {
  UPDATE_PORTAL_USER_DEFAULT_PAYMENT_FREQUENCY,
  UpdatePortalUserDefaultPaymentFrequencyResponseType,
  UpdatePortalUserDefaultPaymentFrequencyVariablesType,
} from 'QuoteApp/services/endpoints/UpdatePortalUserDefaultPaymentFrequency';
import {
  fromSnakeFlexiblePaymentFrequency,
  toSnakeFlexiblePaymentFrequency,
} from 'QuoteApp/services/endpoints/GetPaymentDetails/utils';
import {
  GET_PORTAL_USER_DEFAULT_PAYMENT_FREQUENCY,
  GetPortalUserDefaultPaymentFrequencyResponseType,
} from 'QuoteApp/services/endpoints/GetPortalUserDefaultPaymentFrequency';

type PortalUserPreferencesProviderType = {
  agentPreferredPaymentFrequency: FlexiblePaymentFrequency | null;
  isAgentPreferredPaymentFrequencyLoading: boolean;
  loadAgentPreferredPaymentFrequency: () => Promise<void>;
  saveAgentPreferredPaymentFrequency: (
    frequency: FlexiblePaymentFrequency
  ) => Promise<void>;
};

const SAVE_AGENT_PREFERRED_PAYMENT_FREQUENCY_ERROR_MESSAGE =
  'Unable to save agent payment frequency preference. Please try again.';
const LOAD_AGENT_PREFERRED_PAYMENT_FREQUENCY_ERROR_MESSAGE =
  'Unable to load agent payment frequency preference. Please try again.';

type PendingLoadRequestType = {
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
};

export const usePortalUserPreferences = (): PortalUserPreferencesProviderType => {
  const { toast } = useToast();
  const [portalUserId, setPortalUserId] = useState<string | null>(null);
  const [
    queriedAgentPreferredPaymentFrequency,
    setQueriedAgentPreferredPaymentFrequency,
  ] = useState<FlexiblePaymentFrequency | null>(null);
  const [
    savedAgentPreferredPaymentFrequencyOverride,
    setSavedAgentPreferredPaymentFrequencyOverride,
  ] = useState<FlexiblePaymentFrequency | null>(null);
  const pendingLoadRequest = useRef<PendingLoadRequestType | null>(null);

  const handleLoadAgentPreferredPaymentFrequencySuccess = useCallback(
    (data: GetPortalUserDefaultPaymentFrequencyResponseType) => {
      const currentUser = data.currentUser ?? null;

      setPortalUserId(currentUser?.id ?? null);
      setQueriedAgentPreferredPaymentFrequency(
        fromSnakeFlexiblePaymentFrequency(
          currentUser?.preferences?.agentPreferredPaymentFrequency
        )
      );

      pendingLoadRequest.current?.resolve();
      pendingLoadRequest.current = null;
    },
    []
  );

  const handleLoadAgentPreferredPaymentFrequencyError = useCallback(
    (error: Error) => {
      toast({
        type: 'error',
        message:
          error?.message ||
          LOAD_AGENT_PREFERRED_PAYMENT_FREQUENCY_ERROR_MESSAGE,
        withSecondaryNav: false,
        icon: 'error',
      });

      pendingLoadRequest.current?.reject(error);
      pendingLoadRequest.current = null;
    },
    [toast]
  );

  const [
    fetchPortalUserDefaultPaymentFrequency,
    { loading: isAgentPreferredPaymentFrequencyLoading },
  ] = useLazyQuery<GetPortalUserDefaultPaymentFrequencyResponseType>(
    GET_PORTAL_USER_DEFAULT_PAYMENT_FREQUENCY,
    {
      fetchPolicy: 'network-only',
      onCompleted: handleLoadAgentPreferredPaymentFrequencySuccess,
      onError: handleLoadAgentPreferredPaymentFrequencyError,
    }
  );

  const [updatePortalUserDefaultPaymentFrequency] = useMutation<
    UpdatePortalUserDefaultPaymentFrequencyResponseType,
    UpdatePortalUserDefaultPaymentFrequencyVariablesType
  >(UPDATE_PORTAL_USER_DEFAULT_PAYMENT_FREQUENCY);

  const loadAgentPreferredPaymentFrequency = useCallback(() => {
    if (pendingLoadRequest.current) {
      return pendingLoadRequest.current.promise;
    }

    let resolveLoadRequest: () => void = () => {};
    let rejectLoadRequest: (error: Error) => void = () => {};

    const promise = new Promise<void>((resolve, reject) => {
      resolveLoadRequest = resolve;
      rejectLoadRequest = reject;
    });

    pendingLoadRequest.current = {
      promise,
      resolve: resolveLoadRequest,
      reject: rejectLoadRequest,
    };

    try {
      fetchPortalUserDefaultPaymentFrequency();
    } catch (error) {
      const wrappedError =
        error instanceof Error
          ? error
          : new Error(LOAD_AGENT_PREFERRED_PAYMENT_FREQUENCY_ERROR_MESSAGE);
      handleLoadAgentPreferredPaymentFrequencyError(wrappedError);
    }

    return promise;
  }, [
    fetchPortalUserDefaultPaymentFrequency,
    handleLoadAgentPreferredPaymentFrequencyError,
  ]);

  const agentPreferredPaymentFrequency = useMemo(() => {
    return (
      savedAgentPreferredPaymentFrequencyOverride ??
      queriedAgentPreferredPaymentFrequency
    );
  }, [
    queriedAgentPreferredPaymentFrequency,
    savedAgentPreferredPaymentFrequencyOverride,
  ]);

  const saveAgentPreferredPaymentFrequency = useCallback(
    async (frequency: FlexiblePaymentFrequency) => {
      if (!portalUserId) {
        toast({
          type: 'error',
          message: SAVE_AGENT_PREFERRED_PAYMENT_FREQUENCY_ERROR_MESSAGE,
          withSecondaryNav: false,
          icon: 'error',
        });
        throw new Error(SAVE_AGENT_PREFERRED_PAYMENT_FREQUENCY_ERROR_MESSAGE);
      }

      try {
        await updatePortalUserDefaultPaymentFrequency({
          variables: {
            id: portalUserId,
            portalUser: {
              preferences: {
                defaultPaymentFrequency: toSnakeFlexiblePaymentFrequency(
                  frequency
                ),
              },
            },
          },
        });
        setSavedAgentPreferredPaymentFrequencyOverride(frequency);
      } catch (error) {
        toast({
          type: 'error',
          message:
            (error as Error)?.message ||
            SAVE_AGENT_PREFERRED_PAYMENT_FREQUENCY_ERROR_MESSAGE,
          withSecondaryNav: false,
          icon: 'error',
        });
        throw error;
      }
    },
    [portalUserId, toast, updatePortalUserDefaultPaymentFrequency]
  );

  return {
    agentPreferredPaymentFrequency,
    isAgentPreferredPaymentFrequencyLoading,
    loadAgentPreferredPaymentFrequency,
    saveAgentPreferredPaymentFrequency,
  };
};
